'use client';

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { BonanzaPromoDataApi } from '@/api/cms';
import GeneralSkeleton from '@/components/skeletons/GeneralSkeleton';
import { showTooltip } from '@/helpers/tooltip';
import { useNavigation } from '@/hooks/navigation';
import { useTimeoutRegistrationModal } from '@/hooks/timeoutRegistrationModal';
import { useBonanzaPromo } from '@/modules/promotions';
import { FAQByGroup } from '@/modules/restrictions/components/FAQByGroup';
import { StepsByGroup } from '@/modules/restrictions/components/StepsByGroup';
import { useGetCommonStore } from '@/store';
import { TooltipTypes } from '@/store/tooltipStore';
import { ScContainer } from '@/styles/common';
import { usePromotionsAvailability } from '../../hooks';
import { ScBonanzaPromoPage } from './styled';
import { TopSection } from './TopSection';
import { CtaButton } from './TopSection/CtaButton';

interface BonanzaPromoPageProps {
  data: BonanzaPromoDataApi;
}

const BonanzaPromoPage: React.FC<BonanzaPromoPageProps> = ({ data }) => {
  useTimeoutRegistrationModal();
  const { t } = useTranslation('bonanzaPromo');

  const { isBonanzaPromoAvailable } = usePromotionsAvailability();
  const { isPromoCompleted, bgByStatus } = useBonanzaPromo(data);
  const { navigate } = useNavigation();
  const useCommonStore = useGetCommonStore();
  const isBonanzaInitialized = useCommonStore.use.isBonanzaInitialized();
  useEffect(() => {
    if (!(isBonanzaPromoAvailable || isPromoCompleted)) {
      if (isPromoCompleted) {
        showTooltip({
          type: TooltipTypes.INFO,
          body: t('promoEnded'),
        });
      }

      navigate('/');
    }
  }, [navigate, t, isBonanzaPromoAvailable, isPromoCompleted]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'instant',
      });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, []);

  if (!isBonanzaInitialized) return <GeneralSkeleton />;

  return (
    <ScBonanzaPromoPage>
      <TopSection bgByStatus={bgByStatus} data={data} />
      <ScContainer>
        <div className="content">
          <StepsByGroup
            hideDescription
            sections={data.howToParticipate}
            title={t('howToParticipate')}
          />
          <FAQByGroup sections={data.faq} />
        </div>

        <CtaButton className="lobbyButton" />
      </ScContainer>
    </ScBonanzaPromoPage>
  );
};

export default BonanzaPromoPage;
